import * as Sentry from "@sentry/react";

export function initSentry() {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  // Don't initialize if no DSN or placeholder DSN
  if (!sentryDsn || sentryDsn === 'your_sentry_dsn_here') {
    console.log('[Sentry] Skipped initialization - no valid DSN configured');
    return;
  }

  // Only initialize in production
  if (import.meta.env.PROD) {
    Sentry.init({
      dsn: sentryDsn,

      // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0.1, // 10% of transactions

      // Capture 100% of errors
      sampleRate: 1.0,

      // Set the environment
      environment: import.meta.env.MODE,

      // Release tracking (useful for tracking which version has bugs)
      release: `customer-website@${import.meta.env.VITE_APP_VERSION || 'development'}`,

      // Performance Monitoring
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Session Replay sample rate
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

      // Filter out sensitive information
      beforeSend(event, hint) {
        // Don't send events in development
        if (import.meta.env.DEV) {
          return null;
        }

        // Remove sensitive data from URLs
        if (event.request?.url) {
          event.request.url = event.request.url.replace(/token=[^&]+/gi, 'token=[REDACTED]');
        }

        return event;
      },

      // Ignore certain errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        // Random plugins/extensions
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        // Facebook flakiness
        'fb_xd_fragment',
        // Network errors
        'NetworkError',
        'Network request failed',
        // ResizeObserver loop limit exceeded (harmless)
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
      ],
    });
  } else {
    console.log('[Sentry] Skipped initialization - not in production mode');
  }
}

// Helper to manually capture errors
export function captureException(error: Error, context?: Record<string, any>) {
  try {
    Sentry.captureException(error, {
      extra: context,
    });
  } catch (err) {
    console.error('[Sentry] Not initialized, logging error:', error, context);
  }
}

// Helper to set user context
export function setSentryUser(user: { id: string; email?: string; name?: string } | null) {
  try {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
      });
    } else {
      Sentry.setUser(null);
    }
  } catch (err) {
    // Sentry not initialized, ignore
  }
}

// Helper to add breadcrumbs (trial of what user did before error)
export function addBreadcrumb(message: string, data?: Record<string, any>) {
  try {
    Sentry.addBreadcrumb({
      message,
      data,
      level: 'info',
    });
  } catch (err) {
    // Sentry not initialized, ignore
  }
}
